import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { matrimonyApi } from '../matrimonyApi';

function ProfileCard({ p }) {
  const initials = (name) => (name || 'AB').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  return (
    <div className="m-profile-card">
      <div className="m-profile-card-header">
        <div className="m-profile-card-img">
          {p.profile_photo
            ? <img src={p.profile_photo} alt={p.full_name} />
            : initials(p.full_name)}
        </div>
        <div className="m-profile-card-name">{p.full_name || 'Anonymous'}</div>
        <div className="m-profile-card-meta">
          {p.age && <span>{p.age} yrs</span>}
          {p.city && <><span>·</span><span>📍 {p.city}</span></>}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
          {p.gender && (
            <span className={`m-badge m-badge-${p.gender}`}>{p.gender === 'bride' ? '👰 Bride' : '🤵 Groom'}</span>
          )}
          {p.religion && <span className="m-badge m-badge-gold">{p.religion}</span>}
        </div>
      </div>
      <div className="m-profile-card-body">
        {p.occupation && (
          <div className="m-profile-card-row">
            <span className="m-profile-card-row-icon">💼</span>
            <span>{p.occupation}</span>
          </div>
        )}
        {p.education && (
          <div className="m-profile-card-row">
            <span className="m-profile-card-row-icon">📚</span>
            <span>{p.education}</span>
          </div>
        )}
        {p.about_me && <p className="m-profile-card-about">{p.about_me}</p>}
      </div>
      <div className="m-profile-card-footer">
        <Link to={`/profiles/${p.id}`} className="m-btn m-btn-primary m-btn-sm" style={{ flex: 1, justifyContent: 'center' }}>
          View Profile
        </Link>
      </div>
    </div>
  );
}

export default function MatrimonyHome() {
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading]   = useState(true);

  // Search state
  const [gender, setGender]   = useState('');
  const [ageMin, setAgeMin]   = useState('');
  const [ageMax, setAgeMax]   = useState('');
  const [religion, setReligion] = useState('');

  useEffect(() => {
    matrimonyApi.getProfiles({ size: 6 })
      .then(res => setFeatured(res.data || []))
      .catch(() => setFeatured([]))
      .finally(() => setLoading(false));
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (gender)  params.set('gender', gender);
    if (ageMin)  params.set('ageMin', ageMin);
    if (ageMax)  params.set('ageMax', ageMax);
    if (religion) params.set('religion', religion);
    navigate(`/profiles${params.toString() ? '?' + params.toString() : ''}`);
  };

  return (
    <div>
      {/* Hero */}
      <section className="m-hero">
        <span className="m-hero-icon">💑</span>
        <h1 className="m-hero-title">Find Your Perfect Life Partner</h1>
        <p className="m-hero-subtitle">
          Trusted by thousands of families. Connect with compatible matches from verified profiles across India.
        </p>

        <form className="m-hero-search" onSubmit={handleSearch}>
          <div className="m-form-group" style={{ flex: 1, minWidth: 120, marginBottom: 0 }}>
            <label className="m-form-label">Looking For</label>
            <select className="m-form-control" value={gender} onChange={e => setGender(e.target.value)}>
              <option value="">Any</option>
              <option value="bride">Bride 👰</option>
              <option value="groom">Groom 🤵</option>
            </select>
          </div>
          <div className="m-form-group" style={{ flex: 0, minWidth: 80, marginBottom: 0 }}>
            <label className="m-form-label">Age Min</label>
            <input
              type="number" className="m-form-control" placeholder="18" min="18" max="70"
              value={ageMin} onChange={e => setAgeMin(e.target.value)}
            />
          </div>
          <div className="m-form-group" style={{ flex: 0, minWidth: 80, marginBottom: 0 }}>
            <label className="m-form-label">Age Max</label>
            <input
              type="number" className="m-form-control" placeholder="50" min="18" max="70"
              value={ageMax} onChange={e => setAgeMax(e.target.value)}
            />
          </div>
          <div className="m-form-group" style={{ flex: 1, minWidth: 120, marginBottom: 0 }}>
            <label className="m-form-label">Religion</label>
            <input
              type="text" className="m-form-control" placeholder="Any religion"
              value={religion} onChange={e => setReligion(e.target.value)}
            />
          </div>
          <button type="submit" className="m-btn m-btn-gold" style={{ alignSelf: 'flex-end' }}>
            🔍 Search
          </button>
        </form>
      </section>

      {/* Stats bar */}
      <div className="m-stats-bar">
        <div className="m-stats-bar-inner">
          <div className="m-stats-bar-item">
            <span className="m-stats-bar-num">10,000+</span>
            <span>Verified Profiles</span>
          </div>
          <div className="m-stats-bar-item">
            <span className="m-stats-bar-num">500+</span>
            <span>Successful Matches</span>
          </div>
          <div className="m-stats-bar-item">
            <span className="m-stats-bar-num">100%</span>
            <span>Trusted by Families</span>
          </div>
        </div>
      </div>

      <div className="m-container">
        {/* How it works */}
        <section className="m-section">
          <h2 className="m-section-title">✨ How It Works</h2>
          <p className="m-section-subtitle">Find your life partner in three simple steps</p>
          <div className="m-how-grid">
            <div className="m-how-card">
              <div className="m-how-num">1</div>
              <div className="m-how-title">🌹 Register Free</div>
              <p className="m-how-desc">
                Create your account in minutes. Fill in basic details and submit for admin approval.
              </p>
            </div>
            <div className="m-how-card">
              <div className="m-how-num">2</div>
              <div className="m-how-title">✏️ Complete Your Profile</div>
              <p className="m-how-desc">
                Add detailed personal, family, education and partner preference information to attract the right match.
              </p>
            </div>
            <div className="m-how-card">
              <div className="m-how-num">3</div>
              <div className="m-how-title">💍 Connect &amp; Meet</div>
              <p className="m-how-desc">
                Browse profiles, express interest, and let families connect to find the perfect life partner.
              </p>
            </div>
          </div>
        </section>

        {/* Featured profiles */}
        <section className="m-section">
          <h2 className="m-section-title">🌹 Featured Profiles</h2>
          <p className="m-section-subtitle">Discover recently joined verified members</p>
          {loading ? (
            <div className="m-profiles-grid">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="m-profile-card">
                  <div className="m-profile-card-header">
                    <div className="m-skeleton" style={{ width: 80, height: 80, borderRadius: '50%' }} />
                    <div className="m-skeleton" style={{ width: '70%', height: 18, marginTop: 8 }} />
                    <div className="m-skeleton" style={{ width: '50%', height: 14 }} />
                  </div>
                  <div className="m-profile-card-body">
                    <div className="m-skeleton" style={{ height: 14, width: '80%' }} />
                    <div className="m-skeleton" style={{ height: 14, width: '60%' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : featured.length === 0 ? (
            <div className="m-empty-state">
              <span className="m-empty-state-icon">💑</span>
              <h3>No profiles yet</h3>
              <p>Be the first to register and create your profile!</p>
              <Link to="/register" className="m-btn m-btn-primary">Register Free</Link>
            </div>
          ) : (
            <>
              <div className="m-profiles-grid">
                {featured.map(p => <ProfileCard key={p.id} p={p} />)}
              </div>
              <div style={{ textAlign: 'center', marginTop: 28 }}>
                <Link to="/profiles" className="m-btn m-btn-outline m-btn-lg">
                  View All Profiles →
                </Link>
              </div>
            </>
          )}
        </section>

        {/* CTA */}
        <section className="m-cta-banner">
          <h2 className="m-cta-title">Ready to Find Your Life Partner?</h2>
          <p className="m-cta-sub">Join thousands of families who found their perfect match through SmaBizZ Matrimony</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/register" className="m-btn m-btn-gold m-btn-lg">
              🌹 Register Free Today
            </Link>
            <Link to="/profiles" className="m-btn m-btn-outline m-btn-lg" style={{ borderColor: 'rgba(255,255,255,0.7)', color: '#fff' }}>
              👥 Browse Profiles
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
